/**
 * @fileOverview
 * All variables that get passed in to the mainIssueAggQuery are defined here.
 * The abstraction is necessary because there are multiple call paths to mainIssueAggQuery,
 * so having a single source of truth for the variables eliminates a vector for
 * bugs and inconsistencies when they are invoked.
 *
 * This file will be removed (and therefore the need for the linting exemption) once we reduce
 * the number of discrete call paths for mainIssueAggQuery to one. That work will be done in
 * https://jira.atlassian.com/browse/JIV-19486.
 *
 * Sorry, Tom.
 *
 * <3 Daz
 */
/* eslint-disable jira/ff/inline-usage */
import { LICENSE_STATE_ACTIVE } from '@jira/platform__license-state/src/license-state.tsx';
import { FREE_EDITION } from '@jira/platform__application-editions/src/edition.tsx';
import { expVal, expValEquals } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';
import { JIRA_SOFTWARE } from '@atlassian/jira-shared-types/src/application.tsx';
import { SOFTWARE } from '@atlassian/jira-shared-types/src/application-key.tsx';
import getEditions from '@atlassian/jira-common-util-get-tenant-context/src/edition/index.tsx';
import getLicenseStates from '@atlassian/jira-common-util-get-tenant-context/src/license-state/index.tsx';
import { getLicensedProducts } from '@atlassian/jira-common-util-get-tenant-context/src/index.tsx';
import getMeta from '@atlassian/jira-get-meta';
import {
	CUSTOM_FIELD_MODULE,
	CUSTOM_FIELD_TYPE_MODULE,
	ISSUE_ACTION_MODULE,
	ISSUE_ACTIVITY_MODULE,
	ISSUE_CONTEXT_MODULE,
	ISSUE_GLANCE_MODULE,
	ISSUE_PANEL_MODULE,
	ISSUE_VIEW_BACKGROUND_SCRIPT_MODULE,
	UI_MODIFICATIONS_MODULE,
} from '@atlassian/jira-forge-ui-constants/src/constants.tsx';

import { getPermalinkIdFromURL, COMMENTS } from '../../common/utils/permalinks.tsx';
import mainIssueAggQuery from './__generated__/mainIssueAggQuery$parameters';

type ProvidedVar = (typeof mainIssueAggQuery)['params']['providedVariables'];
type ProvidedVariables = { [k in keyof ProvidedVar]: unknown };

const getMainIssueViewAggProvidedVariables = (): ProvidedVariables => {
	const providerVariables: Record<keyof NonNullable<ProvidedVar>, { get: () => unknown }> =
		mainIssueAggQuery?.params?.providedVariables ?? {};
	return Object.entries(providerVariables).reduce(
		(acc: Record<string, unknown>, [key, provider]) => {
			acc[key] = provider.get();
			return acc;
		},
		{},
	);
};

export const getMainIssueViewAggQueryFeatureFlagVariables = () => ({
	// eslint-disable-next-line jira/relay/no-hardcoded-agg-params
	jsmSentimentCustomFieldFlag: true,
	issueViewDesignsPanelFlag: expVal(
		'collapsed_designs_panel',
		'isCollapsedDesignsPanelEnabled',
		false,
	),
	isFieldsByIdRemoved: fg('dfo_remove_fields_by_id'),
	customFieldConfigurationFlag: fg('issue_view_field_config_edit'),
	// eslint-disable-next-line jira/relay/no-hardcoded-agg-params
	isPinFieldConfigurabilityEnabled: true,
	jcsFlag: fg('jcs_master_flag'),
	// eslint-disable-next-line jira/relay/no-hardcoded-agg-params
	isProactiveCommentSummaryPreferenceEnabled: true,
	// eslint-disable-next-line jira/relay/no-hardcoded-agg-params
	dateFieldsMessageEnabled: true,
	isNavigationMetadataRemoved: fg('dfo_remove_navigation_metadata'),
	issueViewCollapsibleSectionsFieldEnabled: fg('collapsible-sections-in-issue-view-fg'),
	issueViewUserPreferredLayoutFieldEnabled: fg('issue_view_layout_experiment_feature_gate'),
	issueViewDetailsPanelFieldsReorderingEnabled:
		expValEquals(
			'jira-work-item-reordering-fields-v2',
			'reorderingBehaviour',
			'withDragHandler',
			'noReordering',
		) ||
		expValEquals(
			'jira-work-item-reordering-fields-v2',
			'reorderingBehaviour',
			'withoutDragHandler',
			'noReordering',
		),
	issueViewContextPanelFieldsReorderingEnabled: fg('jira-work-item-reordering-fields-v2-fg'),
	isPrioritiesRemoved: fg('dfo_remove_priority_field_from_main_query'),
	issueViewRedactionExperience: fg('enable_redaction_support_in_jira_issue_view'),
	issueViewCoverExperimentEnabled: fg('cover_image_issue_view_experiment_kill_switch'),
	isPlaybooksEnabled: fg('playbook_in_jsm_ga'),
	isPlaybooksEnabledForSpecificType: fg('playbook_visibility_in_service_request'),
	isAutomationDiscoverabilityEnabled: fg('automation_discoverability_in_jsm'),
	issueViewRovoDevAgentsPanelEnabled:
		// These conditions should be included in the conditions for RovodevAgentPanel in content-items-view.tsx
		!fg('boysenberry_enable_entitlements_check') &&
		fg('devai-issue-view-main-migration') &&
		(!!getLicensedProducts(getMeta).devai || fg('rovodev_entitlements_user_access_bypass')),
	issueViewRovoDevAgentsPanelEntitlementEnabled:
		// eslint-disable-next-line jira/ff/no-preconditioning
		fg('boysenberry_rovodev_main_release') && fg('boysenberry_enable_entitlements_check'),
	mentionedConfluencePageLinksV2PaginationFlag:
		// eslint-disable-next-line jira/ff/no-preconditioning
		fg('jsw_issue_view_placeholder_template_v2') && fg('jiv_confluence_remote_link_critical_query'),
	linkedWorkItemsTableEnabled: fg('jira-linked-work-items-targeting-gate'),
	issueViewCommentAvatarFlag: fg('relay-avatar-iv-llc-comments'),
	issueViewSlackSyncInfoEnabled: fg('jira_issue_view_show_slack_sync_info'),
	isConfigUrlDefer: fg('config-url-defer-iv'),
	moveMediaUploadTokenEnabled: fg('dfo_remove_mediacontext_usertoken_from_iviq'),
	isDisableConfigUrlDefer: !fg('config-url-defer-iv'),
	issueViewGoalsFieldWithShowAllGoalsFlag:
		// eslint-disable-next-line jira/ff/no-preconditioning
		fg('jira-preview-panel-goals-integration') &&
		fg('jira_preview_panel_m_1_rollout') &&
		fg('jira-glance-panel'),
	isSkipParentVisibility: fg('remove-parent-visibility-dfo'),
	relayMigrationWebLinksFlag: fg('relay-migration-web-links-fetch'),
	relayMigrationIssueFieldAttachments: fg('relay_migration_issue_field_attachments'),
	relayMigrationChildrenIssuesSectionEnabled:
		// remove-create-subtask-iviq removes canCreateSubTask from issueViewInteractiveQuery and migrates usage to mainIssueAggQuery
		fg('relay_migration_children_issues_section') || fg('remove-create-subtask-iviq'),
	isSkipIssueLinkTypes: fg('remove-issue-link-types-dfo'),
	relayMigrationArchival: fg('relay-migration-archival'),
	relayMigrationIssueComments: fg('relay-migration-issue-comments'),
	issueViewAtlasProjectFieldEnabled: expValEquals(
		'atlassian_projects_-_native_integration',
		'cohort',
		'variation',
		'control',
	),
	isConnectMultiSelectFieldOptionsRemoved: fg('dfo_remove_connect_multi_select_field_options_agg'),
	isConnectSingleSelectFieldOptionsRemoved: fg(
		'dfo_remove_connect_single_select_field_options_agg',
	),
	preloadDevPanelInIssueViewEnabled: fg('preload_dev_panel_in_issue_view'),
	shouldIncludeUserFlag: fg('remove-user-rest-api'),
	consolidateForgeWorkItemPanelsEnabled: fg('nike-dfo-jira-forge-workitempanels'),
	consolidatePermissionsEnabled: fg('nike-dfo-consolidate-permissions'),
	isAssetsFieldAttributesLazyLoadingEnabled: fg('assets-field-attributes-lazy-loading-enabled'),
	isAiAgentSessionsPanelEnabled: fg('jira_assignable_agent_session_experience'),
	consolidateViewIssueCanExportIssue: fg('nike-dfo-consolidate-viewissue-canexportissue'),
	consolidateViewIssueContentPanelsAll: fg('nike-dfo-consolidate-viewissue-contentpanels-all'),
	consolidateAdminPermsEnabled: fg('consolidate-admin-perms-iviq'),
	consolidateEcosystemEnabled: fg('remove-ecosystem-iviq'),
	consolidateUserPickerPermsEnabled: fg('nike-dfo-jira-has-user-picker-permission'),
});

// JIV-19906: retrieve query string from URL -- only expected to be used by client side code
// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
const getURLFromWindow = () => new URL(window.location.href);

// JIV-19906: retrieve query string from url
// server-side: expect url to be passed
// client-side: read from window
// WARNING: ONly a subset of meta tags are available here due to early script execution on ssr failure.
// Please make sure to add any meta tags being used to jira/src/entry-fs/utils/get-pre-html.tsx
export const getMainIssueViewAggQueryVariables = (
	issueKey: string | null,
	url: URL = getURLFromWindow(),
) => ({
	...getMainIssueViewAggProvidedVariables(),
	...getMainIssueViewAggQueryFeatureFlagVariables(),
	...getCommentQueriesFromURL(url),
	...getStorageBannerVariables(),
	...getIssueSearchForChildIssueQueryVariables(issueKey),
	...getForgeSpecificVariables(),
	...getIsFromSoftwareCalendarOrTimelineViewQueryVariables(),
	...getLinkedIssuesTableQueryVariables(issueKey),
	...getStorageHardLimitVariables(),
});

const getIsFromSoftwareCalendarOrTimelineViewQueryVariables = () => {
	if (typeof window !== 'undefined') {
		const pathname = window.location.pathname;
		const pattern = /^\/jira\/software(?:\/c)?\/projects\/[^/]+\/boards\/\d+\/(calendar|timeline)$/;
		return { isInTimelineOrCalendarView: pattern.test(pathname) };
	}
	return { isInTimelineOrCalendarView: false };
};

// Exported for testing
export const getChildIssuesFieldIdsQueryVariables = () => {
	/**
	 * Default fields we're fetching in child issues:
	 * - issuetype: Used to separate base-level and subtasks in epics (see `jira-issue-view-child-issues-panel-merge-subtasks`)
	 */
	const childIssuesFieldIds = ['issuetype'];

	// If we're not fetching the "Include subtasks" timetracking data through Relay
	// include it in `mainIssueAggQuery` so it can be derived from Redux
	if (!fg('relay-migration-edit-worklog') && !fg('jpx-93-unship-legacy-timetracking'))
		childIssuesFieldIds.push('timetracking');

	// If we're not fetching the progress summary status data through Relay,
	// include it in `mainIssueAggQuery` so it can be derived from Redux
	if (!fg('jira-issue-view-child-issues-panel-realtime-update')) childIssuesFieldIds.push('status');

	return childIssuesFieldIds;
};

const getIssueSearchForChildIssueQueryVariables = (issueKey: string | null) => ({
	issueSearchInput: { childIssuesInput: { parentIssueKey: issueKey || '' } },
	fieldSetsInput: {
		viewInput: { namespace: 'CHILD_ISSUE_PANEL', context: { issueContext: { issueKey } } },
	},
	childIssuesFieldIds: getChildIssuesFieldIdsQueryVariables(),
});

const getLinkedIssuesTableQueryVariables = (issueKey: string | null) => ({
	linkedIssuesViewConfigInput: {
		viewInput: { namespace: 'LINKED_ISSUE_PANEL', context: { issueContext: { issueKey } } },
	},
	linkedIssuesFieldSetsInput: {
		viewInput: { namespace: 'LINKED_ISSUE_PANEL', context: { issueContext: { issueKey } } },
	},
	shouldIncludeRemoteIssues: false,
	isEditableSyncLoadedInIssueLinksTable: fg('jira-issue-table-perf-enabled'),
});

export const getCommentQueriesFromURL = (url: URL) => {
	const commentTargetId = getPermalinkIdFromURL(COMMENTS, url);

	return { commentTargetId, hasCommentTargetId: !!commentTargetId };
};

const getStorageBannerVariables = () => ({
	issueViewStorageLimitsBannerFlag: getEditions(getMeta).software === FREE_EDITION,
	productKey: SOFTWARE,
	changeOfferingOfferingName: 'standard',
});

const getForgeSpecificVariables = () => {
	return {
		forgeTypes: [
			ISSUE_ACTION_MODULE,
			ISSUE_GLANCE_MODULE,
			ISSUE_CONTEXT_MODULE,
			ISSUE_PANEL_MODULE,
			ISSUE_ACTIVITY_MODULE,
			CUSTOM_FIELD_MODULE,
			CUSTOM_FIELD_TYPE_MODULE,
			ISSUE_VIEW_BACKGROUND_SCRIPT_MODULE,
			UI_MODIFICATIONS_MODULE,
		],
	};
};

const getStorageHardLimitVariables = () => {
	/* ======================================================================================== */
	/* To fetch attachmentStorageUsed from JiraQuery, using applicationKey: JIRA_SOFTWARE is appropriate. */
	/* The applicationKey is an enum that specifies the application context, and JIRA_SOFTWARE is commonly */
	/* used for Jira Software-related queries. */
	/* ======================================================================================== */

	return {
		applicationKey: JIRA_SOFTWARE,
		issueViewAddAttachmentFlag:
			getEditions(getMeta).software === FREE_EDITION &&
			getLicenseStates(getMeta).software === LICENSE_STATE_ACTIVE &&
			fg('jira-storage-hard-limit-gate'),
	};
};
